import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { bio } from '@/data/projects'
import { generateJsonLd, getCanonicalUrl, getPageMeta } from '@/data/seo'

const MANAGED_META_KEY = 'data-seo-managed'
const JSON_LD_SCRIPT_ID = 'seo-jsonld'

function setMetaTag(selectorAttr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${selectorAttr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(selectorAttr, key)
    el.setAttribute(MANAGED_META_KEY, 'true')
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function clearRepeatedMeta(property: string) {
  document.head
    .querySelectorAll<HTMLMetaElement>(`meta[property="${property}"]`)
    .forEach((el) => el.remove())
}

function appendMeta(selectorAttr: 'name' | 'property', key: string, content: string) {
  const el = document.createElement('meta')
  el.setAttribute(selectorAttr, key)
  el.setAttribute('content', content)
  el.setAttribute(MANAGED_META_KEY, 'true')
  document.head.appendChild(el)
}

function setJsonLd(blocks: object[]) {
  const existing = document.getElementById(JSON_LD_SCRIPT_ID)
  if (existing) existing.remove()

  if (blocks.length === 0) return

  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.id = JSON_LD_SCRIPT_ID
  const payload = blocks.length === 1 ? blocks[0] : blocks
  script.textContent = JSON.stringify(payload)
  document.head.appendChild(script)
}

export function useDocumentHead() {
  const { pathname } = useLocation()

  useEffect(() => {
    const meta = getPageMeta(pathname)
    const canonicalUrl = getCanonicalUrl(meta.canonicalPath)
    const jsonLd = generateJsonLd(pathname)

    document.title = meta.title
    setMetaTag('name', 'description', meta.description)
    setMetaTag('name', 'author', bio.name)
    setMetaTag('name', 'robots', meta.robots)
    setCanonical(canonicalUrl)

    setMetaTag('property', 'og:type', meta.ogType)
    setMetaTag('property', 'og:site_name', bio.name)
    setMetaTag('property', 'og:locale', 'en_US')
    setMetaTag('property', 'og:title', meta.ogTitle)
    setMetaTag('property', 'og:description', meta.ogDescription)
    setMetaTag('property', 'og:image', meta.ogImage)
    setMetaTag('property', 'og:image:width', '1200')
    setMetaTag('property', 'og:image:height', '630')
    setMetaTag('property', 'og:url', canonicalUrl)

    setMetaTag('name', 'twitter:card', 'summary_large_image')
    setMetaTag('name', 'twitter:title', meta.ogTitle)
    setMetaTag('name', 'twitter:description', meta.ogDescription)
    setMetaTag('name', 'twitter:image', meta.ogImage)

    clearRepeatedMeta('article:tag')
    if (meta.articleAuthor) {
      setMetaTag('property', 'article:author', meta.articleAuthor)
    } else {
      document.head.querySelector('meta[property="article:author"]')?.remove()
    }

    if (meta.articlePublishedTime) {
      setMetaTag('property', 'article:published_time', meta.articlePublishedTime)
    } else {
      document.head.querySelector('meta[property="article:published_time"]')?.remove()
    }

    if (meta.articleTags) {
      for (const tag of meta.articleTags) {
        appendMeta('property', 'article:tag', tag)
      }
    }

    setJsonLd(jsonLd)
  }, [pathname])
}
